/**
 * The release 1 game roster.
 *
 * Every game is named after one of Hazel's teddies, alliteratively — the naming
 * rule is what makes a themed title read as deliberate rather than assigned.
 *
 * Chaka's Mastermind is the one exception, chosen deliberately: a parrot is
 * the right teddy to front a game about telling colours apart, and Mickey —
 * the only free teddy that would have alliterated — is a mouse. The rule is a
 * convention rather than a test, and this is what a convention with a reason
 * to break it looks like. Two exceptions would mean it was not a rule.
 */

import type { TeddyKey } from './teddies.js'

/*
 * The order they appear on the picker, and so the number key each one answers
 * to. A new game goes on the end: she has learned that 5 is Small Rabbit's
 * Scramble, and inserting one above it would silently move that.
 */
export const GAME_KEYS = [
  'hangman',
  'maths',
  'memory',
  'trolley',
  'scramble',
  'riddles',
  'sudoku',
  'mosaic',
  'mastermind',
] as const

export type GameKey = (typeof GAME_KEYS)[number]

export interface GameMeta {
  /** Stable identifier used in URLs, database rows and socket messages. */
  readonly key: GameKey
  /** Display title, teddy included. */
  readonly title: string
  /** Which teddy fronts this game, for display. */
  readonly teddy: string
  /** The same teddy, as a key into TEDDIES — used for the title card photo. */
  readonly teddyKey: TeddyKey
  /** One line for the game picker card. */
  readonly blurb: string
}

/*
 * There used to be a `supportsMultiplayer: boolean` here, set on all six games
 * and read by nothing at all. Which games a room can run is decided by
 * `RoomRound` in games/round.ts — a tagged union the compiler checks, so a
 * game with no case there genuinely cannot be started in a room.
 *
 * A flag that looks like the answer and is consulted by nobody is worse than
 * no flag: the next person sets it hopefully, sees nothing happen, and has to
 * read three files to find out why. Deleted rather than wired up, because the
 * union is already the better answer.
 *
 * The room's cards are generated from that union, via `HOW_TO_START` in
 * apps/web/src/pages/hostGames.ts — a `Record<RoomRoundKey, …>` saying how
 * each game is started, since each carries its own settings: a reading band,
 * a board size, three race tiers. Naming the games is not enough to draw
 * them, so saying *how* is what lets the screen come from a list at all.
 *
 * That record is where the compiler stops a game being added to the union and
 * then forgotten on the television.
 */

export const GAMES: Readonly<Record<GameKey, GameMeta>> = {
  hangman: {
    key: 'hangman',
    title: "Hoppity's Hangman",
    teddy: 'Hoppity',
    teddyKey: 'hoppity',
    blurb: 'Guess the word one letter at a time.',
  },
  maths: {
    key: 'maths',
    title: "Monkey's Maths",
    teddy: 'Monkey',
    teddyKey: 'monkey',
    blurb: 'Number puzzles that get trickier as you get better.',
  },
  memory: {
    key: 'memory',
    title: "Sky's Memory Match",
    teddy: 'Sky',
    teddyKey: 'sky',
    blurb: 'Find every pair of teddies.',
  },
  trolley: {
    key: 'trolley',
    title: "Turtley's Trolley",
    teddy: 'Turtley',
    teddyKey: 'turtley',
    blurb: 'Work out the shopping, from the words up.',
  },
  riddles: {
    key: 'riddles',
    title: "Rocky's Riddles",
    teddy: 'Rocky',
    teddyKey: 'rocky',
    blurb: 'Work out who did what, from a handful of clues.',
  },
  scramble: {
    key: 'scramble',
    title: "Small Rabbit's Scramble",
    teddy: 'Small Rabbit',
    teddyKey: 'small-rabbit',
    blurb: 'Unjumble the letters to find the word.',
  },
  sudoku: {
    key: 'sudoku',
    title: "Stripey's Sudoku",
    teddy: 'Stripey',
    teddyKey: 'stripey',
    blurb: 'Fit every teddy into every row, column and box.',
  },
  mosaic: {
    key: 'mosaic',
    title: "Maritza's Mosaic",
    teddy: 'Maritza',
    teddyKey: 'maritza',
    blurb: 'Fill in the squares from the numbers, and find out who it is.',
  },
  mastermind: {
    key: 'mastermind',
    title: "Chaka's Mastermind",
    teddy: 'Chaka',
    teddyKey: 'chaka',
    blurb: 'Work out the hidden row of teddies, a guess at a time.',
  },
}

/** Every game, in the order they should appear on the picker. */
export const ALL_GAMES: readonly GameMeta[] = GAME_KEYS.map((key) => GAMES[key])

export function isGameKey(value: string): value is GameKey {
  return (GAME_KEYS as readonly string[]).includes(value)
}

/** Look up a game by key, tolerating untrusted input from a URL or socket message. */
export function getGame(key: string): GameMeta | undefined {
  return isGameKey(key) ? GAMES[key] : undefined
}
